import Route from '@ember/routing/route'
import { action } from '@ember/object'
import { service } from '@ember/service'
import type Transition from '@ember/routing/transition'
import RouterService from '@ember/routing/router-service'
import { command, context } from 'customer-flows/utils/core'
import { resetAllStores } from 'customer-flows/utils/core'
import { IliadThemeHandler } from '@spinnaker/iliad/dist/utils/iliad-theme-handler'
import Logger from 'customer-flows/utils/logger'
import { normalizeFlowBootError } from 'customer-flows/flow/boot-error'
import { serializeError } from 'customer-flows/utils/serialize-error'
import Extension, { ExtensionStatus } from '@peekops/peek-extensions-framework/Extension'
import FlowExtension from '@peekops/peek-extensions-framework/classes/FlowExtension'
import { mutateConfigData } from 'customer-flows/flow/config'
import type PosthogService from 'customer-flows/services/trackers/posthog'
import env from 'customer-flows/config/environment'

import { FLOW_QUERY_PARAMS } from 'customer-flows/services/flow-router'
export { FLOW_QUERY_PARAMS, type FlowQueryParamName } from 'customer-flows/services/flow-router'

export interface flowRouteQueryParams extends Record<string, unknown> {
  frameId?: string
  key: string
  program_id: string
}

export type flowRouteModel = {
  config: FlowConfig
  key: string
  id: string
} | {
  redirecting: true
}

const V2_FLOW_LAYOUTS = ['TILES_V2', 'BOOKING_V2']

export default class FlowRoute extends Route {
  @service session: Session
  @service router: RouterService
  @service frameManager: FrameManager
  @service legacyNavigator: LegacyNavigator
  @service intentionalNavigation: IntentionalNavigation
  @service eventTracker: EventTracker
  @service flowManager: FlowManager
  @service flowRouter: FlowRouter
  @service('trackers/posthog') posthog: PosthogService

  @command('application') setPartnerKey: ApplicationCommands['setPartnerKey']
  @command('application') startLoading: ApplicationCommands['startLoading']
  @command('application') stopLoading: ApplicationCommands['stopLoading']
  @command('application') fetchRegionComplianceData: ApplicationCommands['fetchRegionComplianceData']

  @command('flow/fetch') fetchFlowConfig: FlowFetchCommands['fetchFlowConfig']
  @command('flow/preflight') runPreflight: FlowPreflightCommands['runPreflight']
  @command('account') fetchAccount: AccountCommands['fetchAccount']
  @command('extendable') fetchExtendables: ExtendablesCommands['fetchExtendables']
  @command('availability') ensureAvailabilityDates: AvailabilityCommands['ensureAvailabilityDates']

  @context('application') partnerKey: ApplicationStore['partnerKey']
  @context('application') legacyProgramId: ApplicationStore['legacyProgramId']
  @context('program') layoutName: ProgramStore['layoutName']
  @context('account') theme: AccountStore['theme']
  @context('account') availabilityBrandingConfig: AccountStore['availabilityBrandingConfig']

  queryParams = FLOW_QUERY_PARAMS

  setTheme = (preloadSuccessImage = true) => {
    const themeName = this.theme ?? ('blue' as AVAILABLE_THEMES)
    const themeUtils = IliadThemeHandler.getInstance()

    if (preloadSuccessImage) {
      const successImageUrl = `/assets/images/success-gifs/${themeName}.gif`

      themeUtils.preloadImages([successImageUrl]).catch((error) => {
        Logger.error('Failed to preload success image:', { error })
      })
    }

    themeUtils.setTheme(themeName)
  }

  @action
  error(error: unknown, _transition: Transition) {
    const bootError = normalizeFlowBootError(error)

    if (this.availabilityBrandingConfig) this.setTheme(false)

    Logger.error('Flow boot failed', {
      errorPage: true,
      key: this.partnerKey,
      programId: this.legacyProgramId,
      retryable: bootError.retryable,
      reason: bootError.reason,
      error: bootError,
      ...serializeError(bootError)
    })

    return true
  }

  private _pendingReplaceWith = false

  deactivate() {
    super.deactivate()
    this.flowManager.resetActiveFlow()
  }

  beforeModel(transition: Transition) {
    if (!transition.from && !this._pendingReplaceWith) {
      this.flowManager.resetActiveFlow()
    }
    if (transition.from) {
      this._pendingReplaceWith = false
    }

    if (!this.flowManager.transitionIsAllowed(transition)) return

    resetAllStores(this)

    const { key } = this.paramsFor('flow') as { key: string }
    this.session.apiKey = key
  }

  async model(params: flowRouteQueryParams, transition: Transition) {
    if (!this.flowManager.transitionIsAllowed(transition)) return

    const { key, program_id: id } = params

    this.setPartnerKey(key)
    this.legacyProgramId = id

    this.startLoading()

    try {
      await Promise.all([
        this.fetchRegionComplianceData(),
        this.fetchAccount()
      ])

      const arrivedViaLegacyTimeout = this.flowRouter.queryParams?.v2via === 'timeout'
      if (!arrivedViaLegacyTimeout) {
        await this.posthog.setup()

        const isControlVariant = this.posthog.isVariantFor('nextgen-conversion', 'control', { sendEvent: false })
        if (isControlVariant === true) {
          this.transitionToLegacy(key, id, transition)
          return { redirecting: true }
        }
      }

      let config: FlowConfig | null = await this.fetchFlowConfig()

    const isV2Flow = V2_FLOW_LAYOUTS.includes(this.layoutName ?? '')

    if (!isV2Flow && !env.isTesting) {
      Logger.warn(`Unsupported flow layout ${this.layoutName}, redirecting to legacy flow`)
      this.transitionToLegacy(key, id, transition)
      return { redirecting: true }
    }

    this.ensureAvailabilityDates()

    try {
      const extendables: Extension[] = await this.fetchExtendables({ programId: config?.program.id })

      const flowConfigExtendables = extendables.filter(
        (extendable) =>
          extendable instanceof FlowExtension &&
          extendable.status === ExtensionStatus.READY &&
          extendable.extendedFlowData
      )

      config = mutateConfigData(config, flowConfigExtendables)
    } catch (error) {
      Logger.error('Failed to load extensions flow', { error })
    }

      this.runPreflight()

      return {
        config,
        key,
        id
      }
    } finally {
      this.stopLoading()
    }
  }

  async afterModel(model: flowRouteModel, transition: Transition) {
    if (!this.flowManager.transitionIsAllowed(transition)) return
    if (model && 'redirecting' in model) return

    const { config, key, id } = model ?? {}
    const params = transition?.to?.queryParams ?? {}

    this.setTheme()

    let registered = false

    try {
      if (!config) {
        throw new Error(`No flow config found for id ${id}`)
      }

      this.startLoading()

      const targetRouteName = await this.flowManager.registerFlow(config, id)

      if (targetRouteName) {
        this._pendingReplaceWith = true
        this.router.replaceWith(targetRouteName, key, id, { queryParams: params })
      }

      registered = true
    } catch (error) {
      if ((error as Error).message === 'TransitionAborted') {
        return
      }

      throw error
    } finally {
      this.stopLoading()

      this.frameManager.postMessage('hide-loading')
    }

    if (!registered) return

    try {
      this.eventTracker.track('Start')
    } catch (error) {
      Logger.error('Failed to track Start event', { error })
    }
  }

  private transitionToLegacy(key: string, id: string, transition: Transition) {
    const params = transition?.to?.queryParams ?? {}

    resetAllStores(this)

    if (this.frameManager.isInFrame) {
      this.intentionalNavigation.viaHost(() =>
        this.frameManager.postMessage('redirect-to-legacy', { key, programId: id, queryParams: params, allowHistory: false })
      )
    } else {
      this.legacyNavigator.transitionToLegacy({
        partnerKey: key,
        programId: id,
        allowHistory: false
      })
    }
  }
}
